/**
 * Cryptographic utilities for password hashing.
 * 
 * Security Flow:
 * 1. Frontend: Hash password with SHA-256 before transmission
 * 2. Backend: Hash the SHA-256 hash with bcrypt for storage
 * 3. Verification: Same process in reverse
 * 
 * This provides:
 * - Protection against password interception (even without HTTPS)
 * - Secure storage with bcrypt
 * - Original password never transmitted or stored
 */

/**
 * Hash a string using SHA-256.
 * Uses the Web Crypto API for secure hashing.
 * 
 * @param {string} message - The string to hash
 * @returns {Promise<string>} - Hexadecimal hash string
 */
export async function sha256(message) {
  // Encode the message as UTF-8
  const msgBuffer = new TextEncoder().encode(message);
  
  // Hash the message using SHA-256
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  
  // Convert the hash to a hexadecimal string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex;
}

/**
 * Hash a password for transmission to the backend.
 * This is the first layer of encryption (SHA-256).
 * The backend will apply bcrypt as the second layer.
 * 
 * @param {string} password - Plain text password
 * @returns {Promise<string>} - SHA-256 hashed password
 */
export async function hashPassword(password) {
  return sha256(password);
}

/**
 * Verify that the crypto API is available.
 * Falls back to a basic implementation if not available.
 * 
 * @returns {boolean} - True if Web Crypto API is available
 */
export function isCryptoAvailable() {
  return typeof crypto !== 'undefined' && crypto.subtle !== undefined;
}

const cryptoUtils = {
  sha256,
  hashPassword,
  isCryptoAvailable,
};

export default cryptoUtils;
